import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Moment from 'moment';
import { payrollTotals, money, ticketStatusLabels, voidedServiceTotal } from './payrollMath';

const HEADER_COLOR = [21, 63, 99];
const empName = (emp) => [emp.mEmployeeFirstName, emp.mEmployeeLastName].filter(Boolean).join(' ') || 'Employee';
const fileSafe = (s) => String(s).replace(/[^A-Za-z0-9_-]+/g, '_');
const rangeText = (from, to) => `${Moment(from).format('MM/DD/YYYY')} - ${Moment(to).format('MM/DD/YYYY')}`;

function drawHeader(doc, title, subtitle) {
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(title, 40, 44);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100);
    doc.text(subtitle, 40, 62);
    doc.text(`Generated ${Moment().format('MM/DD/YYYY hh:mm A')}`, doc.internal.pageSize.getWidth() - 40, 62, { align: 'right' });
    doc.setTextColor(0);
}

// Right-align money columns in the header, body and footer (columnStyles only covers the body).
const rightAlign = (...indexes) => ({
    columnStyles: Object.fromEntries(indexes.map((i) => [i, { halign: 'right' }])),
    didParseCell: (data) => {
        if (indexes.includes(data.column.index)) data.cell.styles.halign = 'right';
    },
});

function addPageNumbers(doc) {
    const pages = doc.getNumberOfPages();
    const width = doc.internal.pageSize.getWidth();
    const height = doc.internal.pageSize.getHeight();
    for (let i = 1; i <= pages; i += 1) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`Page ${i} of ${pages}`, width / 2, height - 20, { align: 'center' });
    }
    doc.setTextColor(0);
}

// All employees in one table, with a totals row.
export function downloadOverallPayrollPdf(employees, from, to) {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    drawHeader(doc, 'Employee Payroll', rangeText(from, to));

    const sum = { service: 0, tips: 0, discounts: 0, offset: 0, net: 0, cash: 0, check: 0 };
    const body = (employees || []).map((emp) => {
        const t = payrollTotals(emp, from, to);
        sum.service += t.service;
        sum.tips += t.tips;
        sum.discounts += t.discounts;
        sum.offset += t.salaryOffset;
        sum.net += t.netPayable;
        sum.cash += t.finalCash;
        sum.check += t.finalCheck;
        return [
            empName(emp),
            emp.mEmployeeRoleName || '-',
            money(t.service),
            money(t.tips),
            money(t.discounts),
            money(t.salaryOffset),
            money(t.netPayable),
            money(t.finalCash),
            money(t.finalCheck),
        ];
    });

    autoTable(doc, {
        startY: 80,
        head: [['Employee', 'Role', 'Commission', 'Tips', 'Discount', 'Salary Offset', 'Total Amount', 'Cash', 'Check']],
        body: body.length ? body : [[{ content: 'No payroll in this period', colSpan: 9, styles: { halign: 'center' } }]],
        foot: body.length ? [[
            'Total', '', money(sum.service), money(sum.tips), money(sum.discounts),
            money(sum.offset), money(sum.net), money(sum.cash), money(sum.check),
        ]] : undefined,
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: HEADER_COLOR },
        footStyles: { fillColor: [235, 239, 244], textColor: 20, fontStyle: 'bold' },
        ...rightAlign(2, 3, 4, 5, 6, 7, 8),
        showFoot: 'lastPage',
        margin: { left: 40, right: 40 },
    });

    addPageNumbers(doc);
    doc.save(`Payroll_${Moment(from).format('YYYY-MM-DD')}_to_${Moment(to).format('YYYY-MM-DD')}.pdf`);
}

// One employee: financial summary, day summary and every service line.
export function downloadEmployeePayrollPdf(emp, tickets, from, to) {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const name = empName(emp);
    drawHeader(doc, `${name} - Payroll`, rangeText(from, to));

    const t = payrollTotals(emp, from, to);
    const rows = tickets || [];
    const voided = voidedServiceTotal(rows);

    const summary = [['Base Service', money((Number(emp.TotalServiceAmount) || 0) + voided)]];
    if (voided > 0) summary.push(['Voided Amount', '-' + money(voided)]);
    summary.push(
        [`Gross Commission (${emp.mEmployeePercentage || 0}%)`, money(t.service)],
        ['Total Tips', '+' + money(t.tips)],
        ['Total Discounts', '-' + money(t.discounts)],
    );
    if (t.hasSalary) {
        summary.push([`Fixed Salary (${emp.salary_type || 'Weekly'})`, money(emp.minimumSalary)]);
        summary.push(['Salary Offset', '+' + money(t.salaryOffset)]);
    }
    summary.push(
        ['Net Payable', money(t.netPayable)],
        [`Cash (${emp.mCashPercentage || 0}%)`, money(t.payCash)],
        [`Check (${emp.mCheckPercentage || 0}%)`, money(t.payCheck)],
        ['Tips by Cash', money(t.tipsCash)],
        ['Tips by Check', money(t.tipsCheck)],
        ['Final Cash Payment', money(t.finalCash)],
        ['Final Check Payment', money(t.finalCheck)],
    );
    const boldRows = new Set(['Net Payable', 'Final Cash Payment', 'Final Check Payment']);

    autoTable(doc, {
        startY: 80,
        head: [['Financial Summary', '']],
        body: summary,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: HEADER_COLOR },
        columnStyles: { 1: { halign: 'right' } },
        tableWidth: 300,
        margin: { left: 40 },
        didParseCell: (data) => {
            if (data.section === 'body' && boldRows.has(data.row.raw[0])) data.cell.styles.fontStyle = 'bold';
        },
    });

    // Day summary
    const days = {};
    rows.forEach((r) => {
        if (!r.ticketDate) return;
        const d = Moment(r.ticketDate).format('YYYY-MM-DD');
        if (!days[d]) days[d] = { service: 0, commission: 0, tips: 0 };
        days[d].service += Number(r.servicePrice) || 0;
        days[d].commission += (r.ticketcommissions || []).reduce((s, c) => s + (Number(c.employeePercentage) || 0), 0);
        days[d].tips += Number(r.Tips) || 0;
    });
    const dayKeys = Object.keys(days).sort();
    if (dayKeys.length) {
        const tot = dayKeys.reduce((a, k) => ({
            service: a.service + days[k].service,
            commission: a.commission + days[k].commission,
            tips: a.tips + days[k].tips,
        }), { service: 0, commission: 0, tips: 0 });
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 20,
            head: [['Date', 'Service', 'Commission', 'Tip']],
            body: dayKeys.map((k) => [Moment(k).format('MM/DD/YYYY ddd'), money(days[k].service), money(days[k].commission), money(days[k].tips)]),
            foot: [['Total', money(tot.service), money(tot.commission), money(tot.tips)]],
            theme: 'striped',
            styles: { fontSize: 9, cellPadding: 4 },
            headStyles: { fillColor: HEADER_COLOR },
            footStyles: { fillColor: [235, 239, 244], textColor: 20, fontStyle: 'bold' },
            ...rightAlign(1, 2, 3),
            showFoot: 'lastPage',
            margin: { left: 40, right: 40 },
        });
    }

    // Service lines
    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Date', 'Ticket', 'Service', 'Price', 'Commission', 'Tip', 'Status']],
        body: rows.length ? rows.map((r) => [
            r.ticketDate ? Moment(r.ticketDate).format('MM/DD/YYYY') : '-',
            r.ticketCode ? `#${r.ticketCode}` : '-',
            r.serviceName || '-',
            money(r.servicePrice),
            (r.ticketcommissions || []).length
                ? r.ticketcommissions.map((c) => (Number(c.employeePercentage) < 0
                    ? `${money(Math.abs(Number(c.employeePercentage)))} (Ded.)`
                    : money(c.employeePercentage))).join('\n')
                : '-',
            money(r.Tips),
            ticketStatusLabels(r.ticket).map(([label]) => label).join(', ') || '-',
        ]) : [[{ content: 'No services in this period', colSpan: 7, styles: { halign: 'center' } }]],
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: HEADER_COLOR },
        ...rightAlign(3, 4, 5),
        margin: { left: 40, right: 40 },
    });

    addPageNumbers(doc);
    doc.save(`Payroll_${fileSafe(name)}_${Moment(from).format('YYYY-MM-DD')}_to_${Moment(to).format('YYYY-MM-DD')}.pdf`);
}
